import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Dispensary } from "custom-types/Dispensary";
import { DailySchedule } from "custom-types/DispensarySchedules";
import useUserFulfillmentPreference from "hooks/useUserFulfillmentPreference";
import { SET_FOR_DELIVERY_OPTION } from "redux/reducers/cart";
import {
  getCartIsInStore,
  getSelectedForDeliveryOption,
} from "redux/selectors/cart";
import {
  getCurrentStatuses,
  getCurrentStatusIsOpenForDelivery,
  getCurrentStatusIsOpenForPickupOrPreorder,
  getDeliveryFee,
  getDeliveryOrderMinimum,
  getDeliveryServiceArea,
  getDispensarySchedules,
  getDispensaryWithDeliveryServiceAreaRanges,
} from "redux/selectors/cartDispensary";
import { getIsValidStreetAddress } from "redux/selectors/location";
import { getIsPickup } from "utils/cartContextUtils";

import FulfillmentToggle from "components/botanic/FulfillmentToggle";
import RetailerDetailsModal from "components/botanic/RetailerDetailsModal";

import DeliveryInfo from "./DeliveryInfo";
import FulfillmentCardPickup from "./FulfillmentCardPickup";

type Props = {
  isEmbedded?: boolean;
};

const FulfillmentCards = ({ isEmbedded }: Props) => {
  const dispatch = useDispatch();
  const dispensary = useSelector(
    getDispensaryWithDeliveryServiceAreaRanges,
  ) as Dispensary;
  const deliveryOrderMinimum = useSelector(getDeliveryOrderMinimum);
  const deliveryFee = useSelector(getDeliveryFee);
  const currentStatuses = useSelector(getCurrentStatuses);
  const deliveryServiceArea = useSelector(getDeliveryServiceArea);
  const selectedForDeliveryOption = useSelector(getSelectedForDeliveryOption);
  const schedules = useSelector(getDispensarySchedules);
  const cartIsInStore = useSelector(getCartIsInStore);

  const openForDelivery = useSelector(getCurrentStatusIsOpenForDelivery);
  const openForPickupOrPreorder = useSelector(
    getCurrentStatusIsOpenForPickupOrPreorder,
  ) as boolean;
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const { userFulfillmentPreference, setUserFulfillmentPreference } =
    useUserFulfillmentPreference();

  const [modalIsVisible, setModalIsVisible] = useState(false);

  useEffect(() => {
    // wait for userFulfillmentPreference value to load before setting FulfillmentToggle
    if (userFulfillmentPreference) {
      const isPickup = getIsPickup(
        openForPickupOrPreorder,
        openForDelivery,
        userFulfillmentPreference,
      );
      handleFulfillmentToggle(isPickup);
    }
  }, [userFulfillmentPreference]);

  const handleFulfillmentToggle = (isPickup: boolean) => {
    const newValue = isPickup ? "pickup" : "delivery";

    setUserFulfillmentPreference(newValue);
    dispatch({
      forDelivery: !isPickup,
      type: SET_FOR_DELIVERY_OPTION,
    });
  };

  return (
    <div className="fulfillment-cards flex flex-col gap-4 min-h-[48px]">
      {!cartIsInStore && (
        <div>
          <div className="text-xs font-extrabold">
            <span>CHOOSE DELIVERY OR PICKUP</span>
          </div>
          <div data-testid="fulfillment-toggle">
            <FulfillmentToggle
              pickupAvailable={currentStatuses?.pickup?.isOpen ?? false}
              alwaysAllowPickup={dispensary.hasReservationsEnabled}
              deliveryAvailable={openForDelivery}
              alwaysAllowDelivery={dispensary.hasDeliveryEnabled}
              preorderAvailable={
                dispensary?.preorderEnabled && !currentStatuses?.store?.isOpen
              }
              deliveryFee={Number(deliveryFee)}
              deliveryMin={Number(deliveryOrderMinimum)}
              fulfillmentCookie={
                selectedForDeliveryOption ? "delivery" : "pickup"
              }
              onClick={handleFulfillmentToggle}
              hasAddress={isValidAddress}
            />
          </div>
        </div>
      )}
      {modalIsVisible && (
        <RetailerDetailsModal
          dispensary={{ ...dispensary, currentStatuses }}
          schedule={schedules?.storeHours as DailySchedule[] | null}
          onDismissed={() => setModalIsVisible(false)}
          deliveryZone={deliveryServiceArea || undefined}
        />
      )}
      {!selectedForDeliveryOption && (
        <React.Fragment>
          <FulfillmentCardPickup
            cartIsInStore={cartIsInStore}
            dispensary={dispensary}
            fulfillmentCardProps={{
              clickHandler: () => setModalIsVisible(true),
              style: { maxWidth: "100%" },
            }}
          />
        </React.Fragment>
      )}
      {!!selectedForDeliveryOption && (
        <DeliveryInfo
          clickHandler={() => setModalIsVisible(true)}
          isEmbedded={isEmbedded}
          className="my-0"
        />
      )}
    </div>
  );
};

export default FulfillmentCards;
